import { useIsMarkAsBookedLiteStorefrontsEnabled } from '@client/hooks/useMarkAsBookedLiteStorefronts';
import { breadcrumbsLevels } from '@components/shared/Breadcrumbs/utils/breadcrumbsLevels';
import { useLiteStorefront } from '@hooks/use-lite-storefront';
import { useAppSelector } from '@redux/hooks';
import { TextSubhead } from '@tkww/orion-web-ds-text';
import { IconButton } from '@xo-union/tk-component-icons';
import cx from 'classnames';
import React, { type CSSProperties, forwardRef, useMemo } from 'react';
import type { StickyChildArgs } from 'react-sticky';
import type { Decorated } from 'types/vendor';
import NavigationHelper from '../../../../../../helpers/navigation';
import { isValidRedirectUrl } from '../../../../../../utils/validateRedirectUrl';
import { StartConversation } from '../../../components/StartConversation/StartConversation';
import SocialSharing from '../../../components/social_sharing';
import BookVendor from '../../vendor_info/components/BookVendor';
import { useIsVendorBooked } from '../../vendor_info/components/BookVendor/hooks/use-is-vendor-booked';
import FavoritesButton from '../../vendor_info/components/favorites_button/FavoritesButton';
import Styles from '../styles.scss';
import type { NavItemElements } from '../types';
import { NavMenuItems } from './NavMenuItems';

interface NavMenuProps extends Pick<StickyChildArgs, 'style' | 'isSticky'> {
	children: NavItemElements;
}

export const NavMenu = forwardRef<HTMLUListElement, NavMenuProps>(
	(props: NavMenuProps, ref) => {
		const { children, style, isSticky } = props;
		const vendor = useAppSelector((state) => state.vendor.vendor as Decorated);
		const viewport = useAppSelector((state) => state.viewport);
		const membership = useAppSelector((state) => state.membership);
		const styles: CSSProperties = {
			...style,
			zIndex: 4,
		};
		const category = useAppSelector((state) => state.category);
		const searchPageLocation = useAppSelector(
			(state) => state.searchPageLocation,
		);
		const state = useAppSelector((state) => state);
		const bookings = useAppSelector((state) => state.bookings.bookings);
		const isVendorBooked = useIsVendorBooked(bookings, vendor.id);

		const isDesktop = viewport.greaterThan.medium;
		const { width, left, ...enhancementStyles } = styles;

		const isLiteStorefront = useLiteStorefront();
		const isMarkAsBookedLiteStorefrontsEnabled =
			useIsMarkAsBookedLiteStorefrontsEnabled();
		const isBookedButtonEnabled =
			vendor.isPaid || isMarkAsBookedLiteStorefrontsEnabled;

		const showFavoriteButtonOnExperiment = useMemo(() => {
			return isBookedButtonEnabled ? !isVendorBooked : true;
		}, [isBookedButtonEnabled, isVendorBooked]);

		const goBack = () => {
			const referrer = NavigationHelper.referrer();
			if (referrer?.includes('/deals') && isValidRedirectUrl(referrer)) {
				/* eslint-disable-next-line snyk/cwe-601 */
				window.location.assign(referrer);
				return;
			}

			const categoryCode = category.code;
			const city = searchPageLocation.city;
			const stateCode = searchPageLocation.stateCode;
			const breadcrumbs = breadcrumbsLevels(
				categoryCode,
				stateCode,
				city,
				vendor,
			);
			const secondFromTheLast = breadcrumbs[breadcrumbs.length - 2];

			if (window.history.length > 1) {
				window.location.assign(secondFromTheLast.url);
			} else {
				window.location.assign('/marketplace');
			}
		};

		return (
			<ul
				style={
					!isLiteStorefront && !isDesktop
						? { left: 0, ...enhancementStyles }
						: styles
				}
				className={cx(Styles.nav, {
					[Styles.stickyNav]: isSticky,
					[Styles.navHide]: !isSticky,
				})}
				id="nav-bar"
				ref={ref}
			>
				<div className={Styles.navBarContainer}>
					{!isLiteStorefront && isSticky && (
						<div className={Styles.vendorContainer}>
							<IconButton
								size="md"
								className={Styles.iconBack}
								aria-label="Go back"
								name="back"
								onClick={goBack}
							/>
							<TextSubhead as="h4" className={Styles.subhead}>
								{vendor?.name}
							</TextSubhead>
						</div>
					)}
					<div className={Styles.navItemMenuContainer}>
						<NavMenuItems>{children}</NavMenuItems>
					</div>
					{isSticky && (
						<li className={cx(Styles.navItem, Styles.saveAndShare)} key="share">
							{!isLiteStorefront && (
								<div className={Styles.conversationButtonContainer}>
									<StartConversation />
								</div>
							)}
							<SocialSharing vendor={vendor} icon="share-ios" />
							{showFavoriteButtonOnExperiment && (
								<div className={Styles.favoritesButtonContainer}>
									<FavoritesButton membership={membership} vendor={vendor} />
								</div>
							)}
							{isBookedButtonEnabled && <BookVendor />}
						</li>
					)}
				</div>
			</ul>
		);
	},
);
